import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { Eye, EyeOff, Moon, Sun } from 'lucide-react';
import BackgroundFX from '../../components/auth/BackgroundFX.jsx';
import LoginBrand, { loginHighlights } from '../../components/auth/LoginBrand.jsx';
import useLoginForm from './useLoginForm.js';
import { Button } from '../../components/ui/button.jsx';
import { Input } from '../../components/ui/input.jsx';
import { Label } from '../../components/ui/label.jsx';
import { useTheme } from '../../theme/ThemeContext.jsx';
import logo from '../../assets/icon.png';
import partyIcon from '../../assets/icon.svg';
import '../../styles/login.css';

export default function Login() {
  const {
    username,
    password,
    rememberMe,
    showPass,
    capsOn,
    errors,
    loading,
    greeting,
    tipOfDay,
    formattedDate,
    formattedTime,
    cooldownUntil,
    feedback,
    handleSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleRememberChange,
    handlePasswordKeyEvent,
    handlePasswordBlur,
    toggleShowPassword,
    acknowledgeFeedback,
  } = useLoginForm();

  const { theme, toggleTheme } = useTheme();
  const [confettiPieces, setConfettiPieces] = useState([]);
  const controls = useAnimation();
  const isDark = theme === 'dark';

  useEffect(() => {
    controls.set({ x: 0 });
  }, [controls]);

  useEffect(() => {
    let timeoutId;

    if (feedback === 'error') {
      controls
        .start({
          x: [-4, 4, -3, 3, -2, 2, 0],
          transition: { duration: 0.6, ease: 'easeInOut' },
        })
        .finally(() => acknowledgeFeedback());
    } else if (feedback === 'success') {
      const pieces = Array.from({ length: 12 }, (_, index) => ({
        id: Date.now() + index,
        left: 24 + Math.random() * 52,
        top: 18 + Math.random() * 14,
        delay: index * 40,
        duration: 900 + Math.random() * 300,
        rotate: Math.random() * 60 - 30,
      }));

      setConfettiPieces(pieces);
      timeoutId = setTimeout(() => {
        setConfettiPieces([]);
        acknowledgeFeedback();
      }, 1300);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [acknowledgeFeedback, controls, feedback]);

  const cooldownSeconds = useMemo(() => {
    if (!cooldownUntil) return 0;
    const diff = cooldownUntil - Date.now();
    return diff > 0 ? Math.ceil(diff / 1000) : 0;
  }, [cooldownUntil]);

  const isSubmitDisabled = loading || cooldownSeconds > 0;
  const highlights = loginHighlights.slice(0, 2);

  return (
    <div className="login-screen relative min-h-screen overflow-x-hidden bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(132,204,22,0.10),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(15,23,42,0.10),transparent_36%)]"
      />
      <BackgroundFX />

      <AnimatePresence>
        {confettiPieces.map(piece => (
          <motion.img
            key={piece.id}
            src={partyIcon}
            alt=""
            initial={{ opacity: 0, y: -20, scale: 0.7, rotate: 0 }}
            animate={{ opacity: 1, y: 80, scale: 1, rotate: piece.rotate }}
            exit={{ opacity: 0, y: 120 }}
            transition={{
              duration: piece.duration / 1000,
              delay: piece.delay / 1000,
              ease: 'easeOut',
            }}
            className="pointer-events-none fixed z-30 h-8 w-8"
            style={{ left: `${piece.left}%`, top: `${piece.top}%` }}
            aria-hidden="true"
          />
        ))}
      </AnimatePresence>

      <div className="login-shell mx-auto flex min-h-screen w-full max-w-[1180px] items-center px-4 py-3 sm:px-5 sm:py-4 lg:px-6">
        <div className="login-grid grid w-full items-center gap-4 lg:grid-cols-[minmax(0,1fr)_392px] xl:grid-cols-[minmax(0,1.02fr)_408px]">
          <section className="login-brand-panel hidden min-h-0 flex-col justify-between rounded-[28px] border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.76)] p-5 shadow-[0_24px_70px_rgba(var(--shadow),0.16)] backdrop-blur lg:flex">
            <div className="login-brand-stack space-y-4">
              <div className="max-w-xl">
                <LoginBrand logo={logo} compact />
              </div>

              <div className="login-highlights-grid grid gap-3 2xl:grid-cols-2">
                {highlights.map(({ icon: Icon, title, description }) => (
                  <div
                    key={title}
                    className="login-highlight-card rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.58)] p-4"
                  >
                    <div className="login-highlight-icon mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand-700))]">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <h3 className="login-highlight-title text-base font-semibold text-[rgb(var(--fg))]">{title}</h3>
                    <p className="login-highlight-copy mt-1 text-sm leading-5 text-[rgb(var(--muted))]">{description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="login-aside-footer grid gap-3 2xl:grid-cols-[1.1fr_0.9fr]">
              <div className="login-shift-card rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.52)] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[rgb(var(--muted))]">
                  Turno actual
                </p>
                <p className="login-shift-title mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">{greeting}</p>
                <p className="login-shift-copy mt-1 text-sm text-[rgb(var(--muted))]">
                  {formattedDate} · {formattedTime}
                </p>
              </div>

              <div className="login-tip-panel login-tip-panel--desktop rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.52)] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[rgb(var(--muted))]">
                  Tip del dia
                </p>
                <p className="login-tip-copy mt-2 text-sm leading-5 text-[rgb(var(--fg))]">{tipOfDay}</p>
              </div>
            </div>
          </section>

          <section className="login-form-column flex min-h-0 items-center justify-center pt-1 sm:pt-2 lg:pt-0">
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.99 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              className="login-panel relative w-full max-w-[408px] rounded-[28px] border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.90)] p-5 shadow-[0_28px_80px_rgba(var(--shadow),0.18)] backdrop-blur md:p-6"
            >
              <motion.div animate={controls} initial={{ x: 0 }}>
                <button
                  type="button"
                  onClick={toggleTheme}
                  aria-pressed={isDark ? 'true' : 'false'}
                  title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                  className="absolute right-3 top-3 z-10 rounded-md p-2 opacity-80 transition-all duration-300 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {isDark ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
                </button>

                <div className="login-mobile-brand mb-4 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.38)] px-3 py-2.5 lg:hidden">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[rgb(var(--muted))]">
                    Sinamon Suite
                  </p>
                  <p className="mt-1 text-sm font-medium text-[rgb(var(--fg))]">
                    Logística operativa con foco en velocidad y control.
                  </p>
                </div>

                <div className="login-panel-header mb-5 flex items-start gap-3 pr-10">
                  <img src={logo} alt="Sinamon" className="login-panel-logo h-14 w-14 shrink-0 rounded-2xl object-contain" />
                  <div className="login-panel-heading min-w-0">
                    <h1 id="login-title" className="text-2xl font-semibold tracking-tight text-[rgb(var(--fg))]">
                      Acceso operativo
                    </h1>
                    <p className="login-panel-subtitle mt-1 text-sm leading-5 text-[rgb(var(--muted))]">
                      Ingresá para continuar con la operación diaria.
                    </p>
                  </div>
                </div>

                <div className="login-status-mobile mb-4 rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.52)] px-3 py-2.5 lg:hidden">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[rgb(var(--muted))]">
                    Estado
                  </p>
                  <p className="mt-1 text-sm font-medium text-[rgb(var(--fg))]">{greeting}</p>
                  <p className="text-xs text-[rgb(var(--muted))]">
                    {formattedDate} · {formattedTime}
                  </p>
                </div>

                <form
                  className="login-form space-y-4"
                  onSubmit={handleSubmit}
                  noValidate
                  role="form"
                  aria-labelledby="login-title"
                >
                  <div className="login-field space-y-1.5">
                    <Label htmlFor="username" className="text-[13px] font-medium text-[rgb(var(--fg))]">
                      Usuario
                    </Label>
                    <Input
                      id="username"
                      name="username"
                      value={username}
                      onChange={handleUsernameChange}
                      placeholder="usuario"
                      autoComplete="username"
                      required
                      aria-label="Usuario corporativo"
                      aria-invalid={Boolean(errors.username)}
                      className="mt-0 min-h-[42px] rounded-xl border-[rgba(var(--border),0.7)] bg-[rgba(var(--bg),0.96)] px-3 py-2 text-[14px]"
                    />
                    <AnimatePresence initial={false}>
                      {errors.username ? (
                        <motion.p
                          key={errors.username}
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          className="text-xs text-rose-500"
                          aria-live="polite"
                          role="alert"
                        >
                          {errors.username}
                        </motion.p>
                      ) : null}
                    </AnimatePresence>
                  </div>

                  <div className="login-field space-y-1.5">
                    <Label htmlFor="password" className="text-[13px] font-medium text-[rgb(var(--fg))]">
                      Contraseña
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPass ? 'text' : 'password'}
                        value={password}
                        onChange={handlePasswordChange}
                        onKeyUp={handlePasswordKeyEvent}
                        onKeyDown={handlePasswordKeyEvent}
                        onBlur={handlePasswordBlur}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        required
                        aria-label="Contraseña"
                        aria-invalid={Boolean(errors.password)}
                        className="min-h-[42px] rounded-xl border-[rgba(var(--border),0.7)] bg-[rgba(var(--bg),0.96)] px-3 py-2 pr-11 text-[14px]"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg text-[rgb(var(--muted))]"
                        onClick={toggleShowPassword}
                        aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        aria-pressed={showPass ? 'true' : 'false'}
                      >
                        {showPass ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                      </Button>
                    </div>
                    <AnimatePresence initial={false}>
                      {capsOn ? (
                        <motion.p
                          key="capslock"
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          className="text-xs font-semibold text-amber-600"
                          aria-live="polite"
                          role="alert"
                        >
                          Bloq Mayús activado
                        </motion.p>
                      ) : null}
                    </AnimatePresence>
                    <AnimatePresence initial={false}>
                      {errors.password ? (
                        <motion.p
                          key={errors.password}
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          className="text-xs text-rose-500"
                          aria-live="polite"
                          role="alert"
                        >
                          {errors.password}
                        </motion.p>
                      ) : null}
                    </AnimatePresence>
                  </div>

                  <div className="login-meta-row flex items-center justify-between gap-3">
                    <label htmlFor="rememberMe" className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-[rgb(var(--muted))]">
                      <input
                        id="rememberMe"
                        name="rememberMe"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={handleRememberChange}
                        className="h-4 w-4 rounded border-[rgba(var(--border),0.9)] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                      />
                      Recordarme
                    </label>
                    <span className="text-[11px] text-[rgb(var(--muted))]">Acceso seguro</span>
                  </div>

                  <div className="login-submit-block space-y-2" aria-live="polite">
                    <button
                      type="submit"
                      disabled={isSubmitDisabled}
                      className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-[rgba(var(--brand),0.78)] px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-[rgb(var(--brand-700))] hover:text-white focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? (
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity=".25" />
                          <path d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" fill="none" />
                        </svg>
                      ) : null}
                      <span>{loading ? 'Ingresando...' : 'Ingresar'}</span>
                    </button>

                    {cooldownSeconds > 0 ? (
                      <p className="text-center text-xs font-medium text-amber-600">
                        Esperá {cooldownSeconds}s para volver a intentar.
                      </p>
                    ) : null}
                  </div>
                </form>

                <div className="login-tip-panel login-tip-panel--mobile mt-4 rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.48)] px-3 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[rgb(var(--muted))]">
                    Tip del dia
                  </p>
                  <p className="login-tip-copy mt-1 text-xs leading-5 text-[rgb(var(--fg))]">{tipOfDay}</p>
                </div>
              </motion.div>
            </motion.div>
          </section>
        </div>
      </div>
    </div>
  );
}
